// Card vertical de item do carrinho — compartilhado entre Mesas, Balcão e Delivery (CardapioPopup).
// Layout: header horizontal (foto + nome + preço + controles), corpo vertical com observação
// e subitens agrupados por step_name (Sabores, Borda, Adicionais, etc.) com mini-fotos via lookup.
// Subitens sem foto (ex: bordas de pizza) não mostram placeholder cinza — só nome e preço.
import React from "react";
import { Button } from "../ui/button";
import { Minus, Plus, Trash2, X, Pencil } from "lucide-react";
import { formatarQtd, medidaDoItem } from "../../lib/unidadeVenda";
import { precoDaOpcao } from "../../utils/precoEtapas";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("data:") || url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/uploads/")) return `/api${url}`;
  return url;
};

function buildStepsSummary(item, allProducts) {
  const allProds = Array.isArray(allProducts) ? allProducts : [];
  // Caminho 1: subitems já normalizados (pizza, re-order, etc.)
  if (Array.isArray(item.subitems) && item.subitems.length > 0) {
    const grouped = {};
    const order = [];
    item.subitems.forEach((si) => {
      const k = (si.step_name && String(si.step_name).trim()) || "Adicionais";
      if (grouped[k] === undefined) { grouped[k] = []; order.push(k); }
      grouped[k].push(si);
    });
    return order.map((name) => ({
      stepName: name,
      items: grouped[name].map((si, idx) => {
        const lookup = si.product_id ? allProds.find((p) => String(p.id) === String(si.product_id)) : null;
        return {
          id: si.product_id || `${name}-${idx}`,
          name: si.nome || si.name || "",
          photo_url: si.photo_url || lookup?.photo_url || null,
          price: si.preco != null ? Number(si.preco) : (si.price != null ? Number(si.price) : null),
          qty: si.quantidade || si.qty || 1,
        };
      }).filter((x) => x.name),
    })).filter((s) => s.items.length > 0);
  }
  // Caminho 2: combo com step_selections + order_steps
  if (item.step_selections && Array.isArray(item.order_steps)) {
    const steps = item.order_steps.filter((s) => (item.combo_type === "simples" ? !s.combo_only : true));
    const out = [];
    steps.forEach((step, idx) => {
      const ids = item.step_selections[idx] || [];
      const items = ids.map((id) => {
        const stepItem = step.items?.find((i) => i.product_id === id);
        const fullProd = allProds.find((p) => p.id === id);
        const name = stepItem?.product_name || fullProd?.name || "";
        // Mesmo critério do popup: incluso com price_override 0 é grátis (achado 361)
        const price = precoDaOpcao(step, stepItem, fullProd);
        return {
          id,
          name,
          photo_url: stepItem?.photo_url || fullProd?.photo_url || null,
          price,
          gratis: price === 0,
          qty: 1,
        };
      }).filter((x) => x.name);
      if (items.length) out.push({ stepName: step.name, items });
    });
    return out;
  }
  // Caminho 3: etapas denormalizadas (re-order legado)
  if (Array.isArray(item.etapas) && item.etapas.length > 0) {
    return item.etapas.map((etapa) => {
      const names = Array.isArray(etapa.itens) ? etapa.itens : Array.isArray(etapa.items) ? etapa.items : [];
      return {
        stepName: etapa.etapa || etapa.stepName || "",
        items: names.map((nm, i) => {
          const prod = allProds.find((p) => (p.name || "").toLowerCase() === String(nm || "").toLowerCase());
          return {
            id: prod?.id || `${nm}-${i}`,
            name: nm,
            photo_url: prod?.photo_url || null,
            price: prod?.sale_price ?? null,
            qty: 1,
          };
        }).filter((x) => x.name),
      };
    }).filter((s) => s.items.length > 0);
  }
  return [];
}

export default function CartItemCard({
  item,
  allProducts = [],
  onIncrement,
  onDecrement,
  onRemove,
  onEdit,
  readonly = false,
  className = "",
}) {
  const unitPrice = item.final_price ?? item.sale_price ?? 0;
  const stepsSummary = buildStepsSummary(item, allProducts);
  const qtyIsOne = (item.quantity === 1 || Number(item.quantity) === 1) && !item.is_divisible;
  // Quilo/litro pela regra única (kg/L + divisível, pelo cadastro quando vier):
  // "0,350 kg". "Guaraná 2 L" vendido inteiro é por unidade — sem sufixo.
  const medida = medidaDoItem(item, allProducts);
  const [qtdTexto, qtdUnidade] = medida ? formatarQtd(item.quantity, medida).split(" ") : [formatarQtd(item.quantity, null), null];

  return (
    <div className={`flex flex-col gap-2 p-3 bg-background rounded-lg border ${className}`}>
      {/* Header: foto + nome/preço + controles */}
      <div className="flex items-start gap-2">
        <div className="w-12 h-12 bg-muted rounded-lg flex-shrink-0 overflow-hidden">
          {item.photo_url ? (
            <img src={getImageUrl(item.photo_url)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-base">🍽️</div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 flex-wrap">
            <p className="font-medium text-sm leading-tight">{item.name}</p>
            {item.combo_type && (
              <span className={`text-[9px] px-1 py-0.5 rounded ${item.combo_type === "combo" ? "bg-green-500/20 text-green-700 dark:text-green-400" : "bg-blue-500/20 text-blue-600 dark:text-blue-400"}`}>
                {item.combo_type === "combo" ? "COMBO" : "SIMPLES"}
              </span>
            )}
          </div>
          <p className="text-sm text-primary font-bold mt-0.5">
            R$ {(unitPrice * (item.quantity || 1)).toFixed(2).replace(".", ",")}
          </p>
        </div>
        {readonly ? (
          <span className="text-xs font-bold text-orange-500 flex-shrink-0 whitespace-nowrap">{medida ? formatarQtd(item.quantity, medida) : `×${item.quantity}`}</span>
        ) : (
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Editar (v1.5.469): reabre o popup do produto com as escolhas do
                item pra ajustar etapas/observação sem apagar e recomeçar. */}
            {onEdit && (
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 sm:h-7 sm:w-7"
                onClick={onEdit}
                title="Editar item"
              >
                <Pencil className="w-3 h-3" />
              </Button>
            )}
            {qtyIsOne && onRemove ? (
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 sm:h-7 sm:w-7 border-red-500/50 text-red-500 hover:bg-red-500/10 hover:text-red-600"
                onClick={onRemove}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            ) : (
              <Button variant="outline" size="icon" className="h-9 w-9 sm:h-7 sm:w-7" onClick={onDecrement}>
                <Minus className="w-3 h-3" />
              </Button>
            )}
            <span className="min-w-[1.5rem] text-center text-sm">
              {qtdTexto}
              {qtdUnidade && (
                <span className="text-[10px] text-muted-foreground ml-0.5">{qtdUnidade}</span>
              )}
            </span>
            <Button variant="outline" size="icon" className="h-9 w-9 sm:h-7 sm:w-7" onClick={onIncrement}>
              <Plus className="w-3 h-3" />
            </Button>
            {!qtyIsOne && onRemove && (
              <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-7 sm:w-7 text-destructive" onClick={onRemove}>
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Observação */}
      {item.observation && (
        <p className="text-xs text-orange-500 break-words">📝 {item.observation}</p>
      )}

      {/* Subitens agrupados por etapa — bordas sem foto não mostram placeholder */}
      {stepsSummary.length > 0 && (
        <div className="space-y-2 mt-1">
          {stepsSummary.map((step, gi) => (
            <div key={gi}>
              <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                {step.stepName}
              </p>
              <div className="space-y-1">
                {step.items.map((si, i) => (
                  <div key={i} className="flex items-center gap-2">
                    {si.photo_url && (
                      <div className="w-7 h-7 rounded-md bg-muted flex-shrink-0 overflow-hidden">
                        <img
                          src={getImageUrl(si.photo_url)}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] truncate leading-tight">
                        {si.qty > 1 && <span className="text-muted-foreground mr-0.5">{si.qty}×</span>}
                        {si.name}
                      </p>
                      {si.price != null && si.price > 0 && (
                        <p className="text-[10px] text-orange-500 font-medium leading-tight">
                          R$ {si.price.toFixed(2).replace(".", ",")}
                        </p>
                      )}
                      {si.gratis && (
                        <p className="text-[10px] text-green-500 font-medium leading-tight">Grátis</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
